<template>
  <ul class="lists-section px-0 mb-0">
    <li
      class="caret-link nav-toplevel nav-toplevel-item nav-item position-relative pointer d-flex align-items-center"
      @click="toggleListsVisibility"
      id="lists-nav-accordion"
    >
      <collapse-caret
        class="flex-shrink-0 collapse-section"
        :size="16"
        :container-to-icon-ratio="0.5"
        :expanded="showListLinks"
      />
      <div
        class="d-flex align-items-center justify-content-between flex-grow-1 plus-icon-container"
      >
        <div class="nav-top-link">
          <i class="reports-icon"></i>
          <span class="py-1">Views</span>
        </div>
        <action-icon
          icon="plus-icon"
          class="plus-icon-component"
          tooltip-text="Create a new View"
          @click.stop="navigateToListCreation"
        />
      </div>
    </li>
    <b-collapse v-model="showListLinks" class="lists">
      <div class="no-list text-small-1 text-secondary" v-if="isEmptyUserList">
        <b-button
          variant="link"
          class="text-secondary text-medium p-0 pl-1 text-left"
          @click.stop="navigateToListCreation"
          >Add view
        </b-button>
      </div>
      <ul
        class="nav flex-column px-0"
        @click="$emit('close')"
        ref="listContainer"
        :key="listKey"
        v-else
      >
        <li
          class="nav-item nav-sublevel d-flex list-link"
          :data-id="list.id"
          v-for="list in savedUserLists"
          :key="list.id"
        >
          <i class="gripper-icon" v-if="allowDragDrop"></i>
          <router-link
            :active-class="activeList === list.id ? 'router-link-active' : 'router-link-inactive'"
            class="nav-top-link d-flex align-items-center radial-hover"
            :to="getListLocation(list)"
          >
            <span class="text-truncate" :title="list.name">{{ list.name }}</span>
          </router-link>
        </li>
        <action-tooltip
          placement="right"
          target="all-initiatives-nav"
          text="All Initiatives"
          :actions="['i']"
        />
      </ul>
    </b-collapse>
  </ul>
</template>

<script>
import ActionTooltip from '@/components/ActionTooltip'
import CollapseCaret from '@/components/CollapseCaret'
import ActionIcon from '@/components/ActionIcon.vue'
import events from '@/constants/events'
import { explorePageTabsIds } from '@/constants/explore'
import { ROUTE_NAMES } from '@/constants/routes'
import { STORAGE_KEYS } from '@/constants/storage'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import SidebarStorageMixins from '@/mixins/SidebarStorageMixins'
import UserListsMixin from '@/mixins/UserListsMixin'
import handleClickNavigation from '@/utils/handleClickNavigation'
import { getListCreationRouteConfig } from '@/utils/navigationConfigs'
import { BButton, BCollapse } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions, mapGetters } from 'vuex'

/**
 * This renders the list pages links in the sidebar
 */
export default {
  name: 'UserListsLinks',
  props: ['orgId', 'subOrgId'],
  mixins: [EventBusMixin, UserListsMixin, SidebarStorageMixins, DragAndDropMixin, MediaQueryMixin],
  components: {
    ActionTooltip,
    CollapseCaret,
    BCollapse,
    BButton,
    ActionIcon
  },
  data() {
    return {
      ROUTE_NAMES,
      showListLinks: this.getSidebarFlag(STORAGE_KEYS.sidebar.flags.listsExpanded),
      listKey: 0
    }
  },
  computed: {
    ...mapGetters('userLists', ['userLists']),
    allowDragDrop() {
      return !this.isMobileMode
    },
    userId() {
      return this.$store.state.user.id
    },
    activeList() {
      return this.getActiveListId(this.$route.query)
    },
    listNameInURL() {
      return this.$route.query.list
    },
    savedUserLists() {
      const teamId = this.subOrgId
      return this.userLists(teamId) || []
    },
    isEmptyUserList() {
      return this.savedUserLists.length === 0
    }
  },
  methods: {
    ...mapActions('userLists', ['getUserLists', 'createUserList']),
    ...mapActions(['changeRank']),
    async refreshList() {
      const teamId = this.subOrgId
      const userLists = await this.getUserLists({
        orgId: this.orgId,
        teamId: teamId
      })

      this.enableListDragDrop()
      return userLists
    },
    async onDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: this.orgId,
          type: 'user_list',
          subjectId,
          beforeId,
          afterId
        })
      } catch {
        this.listKey++
        this.enableListDragDrop()
      }
    },
    enableListDragDrop() {
      this.$nextTick(() => {
        if (this.allowDragDrop && this.savedUserLists.length > 0) {
          this.enableDragAndDrop({
            containers: [this.$refs.listContainer],
            onDrop: this.onDrop,
            isFullyDraggable: true
          })
        }
      })
    },
    async migrateUserLists() {
      if (!this.getListMigrationUpdateFlag(this.userId)) {
        const initiativeLists = this.getUserAllInitiativeLists(this.userId)
        const response = initiativeLists.map(list => {
          return this.postUserList(list)
        })
        const migrationResponse = await Promise.all(response)
        let migrationError = !migrationResponse.every(Boolean)
        if (initiativeLists && initiativeLists.length > 0 && !migrationError) {
          this.setListMigrationUpdateFlag(this.userId)
        }
      }
    },
    async postUserList(list) {
      let migrationSuccess = false
      let team = this.$store.getters.teamById(list.teamId)
      let payload = {
        organizationId: team.organizationId,
        name: list.name,
        filterData: R.omit(['list'], list.filterData),
        teamId: list.teamId,
        entityType: 'initiative'
      }
      try {
        await this.createUserList(payload)
        migrationSuccess = true
      } catch (e) {
        console.log('Duplicate Key Found', e, e.response.status)
        if (e.response.status == 409) {
          migrationSuccess = true
        }
      }
      return migrationSuccess
    },
    toggleListsVisibility() {
      this.showListLinks = !this.showListLinks
      this.setSidebarFlag(STORAGE_KEYS.sidebar.flags.listsExpanded, this.showListLinks)
    },
    navigateToListCreation(event) {
      handleClickNavigation.call(
        this,
        event,
        getListCreationRouteConfig({
          orgId: this.orgId,
          subOrgId: this.subOrgId,
          entity: explorePageTabsIds.initiatives
        })
      )
    }
  },
  async mounted() {
    // TODO: remove code related to migrateUserLists
    await this.migrateUserLists()
    this.refreshList()

    this.listenToEventBus(events.USER_LIST_CHANGED, () => {
      this.refreshList()
    })
  },
  watch: {
    async subOrgId() {
      this.refreshList()
    },
    allowDragDrop: 'enableListDragDrop'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_hoverStates.scss';
@import 'styles/rearrangableSidebarLinks';

.no-list {
  padding-left: 28px;
  padding-bottom: 8px;
  padding-right: 12px;
  min-height: 50px;
}
.collapse-section,
.plus-icon-component {
  opacity: 0;
}
.caret-link:hover .collapse-section,
.plus-icon-container:hover .plus-icon-component {
  opacity: 1;
}
</style>
